import EmblaCarousel from 'embla-carousel';

/**
 * Project gallery carousel — Alpine wrapper around two linked Embla instances.
 *
 * Main viewport: one slide per gallery item (image, self-hosted video, or
 * Vimeo/YouTube embed). Thumbs viewport: clickable buttons that scroll the
 * main carousel. CSP-safe: no inline expressions with arguments — thumbnail
 * buttons carry data-thumb-index and listeners are attached in init().
 *
 * Video autoplay:
 *   The focused slide's video plays muted; non-focused videos are paused and
 *   reset to 0. HTML5 videos use .play()/.pause(); Vimeo and YouTube iframes
 *   are controlled via postMessage. Iframe commands are deferred until each
 *   iframe's load event fires.
 */
// When a project has `card_image_override` set, the template prepends the
// override as slide 0 so the forward FLIP morph collapses into the same image
// the user clicked. After this delay the carousel auto-advances to slide 1
// (the project's true first gallery item) so the override doesn't permanently
// occupy the "primary" position. Long enough that the FLIP morph + bloom (~1s
// total) is finished before the carousel starts moving.
const OVERRIDE_ADVANCE_DELAY_MS = 1000;

export default function projectGalleryCarousel() {
    return {
        main: null,
        thumbs: null,
        active: 0,
        count: 0,
        _thumbButtons: [],
        _overrideAdvanceTimer: null,

        init() {
            const mainViewport = this.$refs.main;
            const thumbsViewport = this.$refs.thumbs;
            if (!mainViewport) {
                return;
            }

            this.count = mainViewport.querySelectorAll('.embla__slide').length;
            if (this.count <= 1) {
                return;
            }

            this.main = EmblaCarousel(mainViewport, {
                loop: true,
                align: 'start',
                duration: 25,
            });

            // Override-in-use: auto-advance off slide 0 after the FLIP morph
            // has had time to settle. Skipped on prefers-reduced-motion so
            // the user isn't surprised by movement they didn't trigger.
            const hasOverride = mainViewport.dataset.cardImageOverride === 'true';
            const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            if (hasOverride && this.count > 1 && !reducedMotion) {
                this._overrideAdvanceTimer = window.setTimeout(() => {
                    this._overrideAdvanceTimer = null;
                    this.main?.scrollTo(1);
                }, OVERRIDE_ADVANCE_DELAY_MS);
            }

            if (thumbsViewport) {
                this.thumbs = EmblaCarousel(thumbsViewport, {
                    containScroll: 'keepSnaps',
                    dragFree: true,
                });

                this._thumbButtons = Array.from(
                    thumbsViewport.querySelectorAll('[data-thumb-index]'),
                );
                this._thumbButtons.forEach((btn) => {
                    const index = parseInt(btn.dataset.thumbIndex, 10);
                    btn.addEventListener('click', () => this.scrollTo(index));
                });
            }

            // Track when each iframe player has loaded — postMessage commands
            // sent before load are dropped by both Vimeo and YouTube. The
            // load handler re-runs the sync so the initial active slide
            // starts playing once its iframe is ready.
            mainViewport.querySelectorAll('iframe[data-video-provider]').forEach((iframe) => {
                iframe.dataset.playerLoaded = 'false';
                iframe.addEventListener('load', () => {
                    iframe.dataset.playerLoaded = 'true';
                    this._syncVideoPlayback();
                });
            });

            this.main.on('select', this.onMainSelect.bind(this));
            this.main.on('reInit', this.onMainSelect.bind(this));
            this.onMainSelect();
        },

        onMainSelect() {
            if (!this.main) {
                return;
            }
            this.active = this.main.selectedScrollSnap();
            this.thumbs?.scrollTo(this.active);
            this._updateThumbStates();
            this._syncVideoPlayback();
        },

        _updateThumbStates() {
            this._thumbButtons.forEach((btn, i) => {
                const isActive = i === this.active;
                btn.classList.toggle('is-active', isActive);
                btn.setAttribute('aria-current', isActive ? 'true' : 'false');
            });
        },

        _syncVideoPlayback() {
            if (!this.$refs.main) {
                return;
            }
            const slides = this.$refs.main.querySelectorAll('.embla__slide');
            slides.forEach((slide, i) => {
                const isActive = i === this.active;
                const video = slide.querySelector('video[data-video-provider="file"]');
                const iframe = slide.querySelector('iframe[data-video-provider]');
                if (video) {
                    this._syncHtmlVideo(video, isActive);
                }
                if (iframe) {
                    this._syncEmbed(iframe, isActive);
                }
            });
        },

        _syncHtmlVideo(video, isActive) {
            if (isActive) {
                video.muted = true;
                try {
                    video.currentTime = 0;
                } catch (_) {
                    /* readyState too low — play() will start from 0 anyway */
                }
                const p = video.play();
                if (p && typeof p.catch === 'function') {
                    p.catch(() => { /* autoplay blocked; user can press play */ });
                }
            } else {
                video.pause();
                try {
                    video.currentTime = 0;
                } catch (_) { /* noop */ }
            }
        },

        _syncEmbed(iframe, isActive) {
            // Defer until the player has loaded — early postMessages are dropped.
            if (iframe.dataset.playerLoaded !== 'true') {
                return;
            }
            const provider = iframe.dataset.videoProvider;
            if (provider === 'vimeo') {
                if (isActive) {
                    this._postVimeo(iframe, { method: 'setMuted', value: true });
                    this._postVimeo(iframe, { method: 'setCurrentTime', value: 0 });
                    this._postVimeo(iframe, { method: 'play' });
                } else {
                    this._postVimeo(iframe, { method: 'pause' });
                    this._postVimeo(iframe, { method: 'setCurrentTime', value: 0 });
                }
                return;
            }
            if (provider === 'youtube') {
                if (isActive) {
                    this._postYoutube(iframe, { event: 'command', func: 'mute', args: [] });
                    this._postYoutube(iframe, { event: 'command', func: 'seekTo', args: [0, true] });
                    this._postYoutube(iframe, { event: 'command', func: 'playVideo', args: [] });
                } else {
                    this._postYoutube(iframe, { event: 'command', func: 'pauseVideo', args: [] });
                    this._postYoutube(iframe, { event: 'command', func: 'seekTo', args: [0, true] });
                }
            }
        },

        _postVimeo(iframe, msg) {
            try {
                iframe.contentWindow?.postMessage(JSON.stringify(msg), 'https://player.vimeo.com');
            } catch (_) { /* origin mismatch / detached frame */ }
        },

        _postYoutube(iframe, msg) {
            try {
                iframe.contentWindow?.postMessage(JSON.stringify(msg), 'https://www.youtube-nocookie.com');
            } catch (_) { /* origin mismatch / detached frame */ }
        },

        scrollTo(index) {
            this.main?.scrollTo(index);
        },

        scrollNext() {
            this.main?.scrollNext();
        },

        scrollPrev() {
            this.main?.scrollPrev();
        },

        destroy() {
            if (this._overrideAdvanceTimer) {
                window.clearTimeout(this._overrideAdvanceTimer);
                this._overrideAdvanceTimer = null;
            }
            this.main?.destroy();
            this.thumbs?.destroy();
            this.main = null;
            this.thumbs = null;
            this._thumbButtons = [];
        },
    };
}
